<script lang="ts">
  /* Self-serve signup. Posts to the public POST /access-requests endpoint
     (the same flow superadmin approval consumes). Company type is chosen
     here and drives the per-type onboarding the user sees after they accept
     the welcome invitation.

     TODO(pricing/subscriptions): no plan is chosen here on purpose — users go
     straight to their primary goal and only meet pricing/plan limits later.
     The Pricing (Тарифы) page and the Plan/Subscription model are out of
     scope for now; see specs/TODO_pricing_subscriptions.md. */
  import { _ } from "svelte-i18n";
  import { onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import LanguageSwitcher from "$components/LanguageSwitcher.svelte";
  import AuthHero from "./AuthHero.svelte";
  import ConsentText from "$components/ConsentText.svelte";
  import { api, ApiError } from "$lib/api";
  import { setPendingVerificationEmail } from "$lib/verifyEmail";
  import { fetchCountries, type CountryRef } from "$lib/catalog";
  import { countryAlpha2, localizedCountryOptions } from "$lib/countries";
  import { detectCountry } from "$lib/geo";
  import { locale, LANGUAGES } from "$lib/i18n";

  /* The company type is NOT asked here — the first onboarding screen (right
     after signup) is the single place where the user picks it. We still have
     to send *something* the tenant can be created with, so the account starts
     as a manufacturer and the onboarding role screen patches it. */
  const DEFAULT_COMPANY_TYPE = "manufacturer";

  let companyName = $state("");
  let fullName = $state("");
  /* The short form of a client invitation (owner, 2026-09-19) asks for the
     manager by name, surname and phone, and one question: only order from
     the supplier that invited us, or that plus a look at the whole system. */
  let firstName = $state("");
  let lastName = $state("");
  let phone = $state("");
  let workspace = $state<"orders" | "explore" | "">("");
  let email = $state("");
  let password = $state("");
  let confirmPassword = $state("");
  let showPassword = $state(false);
  let showConfirm = $state(false);
  /* Only complain about a mismatch once the user has actually typed into the
     second field — flagging an error on the first keystroke reads as broken. */
  const passwordsMismatch = $derived(
    confirmPassword.length > 0 && password !== confirmPassword,
  );
  let country = $state("CZ");
  /* Cleared as soon as the user opens the dropdown themselves, so a slow
     IP lookup can never overwrite a deliberate choice. */
  let countryAutoDetect = $state(true);

  let countries = $state<CountryRef[]>([]);
  let countryOptions = $derived(localizedCountryOptions(countries, $locale ?? "en"));
  let loading = $state(false);
  let error = $state<string | null>(null);
  let acceptTerms = $state(false);

  /* A partner-invitation e-mail lands here as /#/register?email=…&company=…&
     country=…&lr=…&lang=…. The params prefill the form and `lr` rides along
     to signup, so the invitation is waiting in the new tenant's inbox the
     moment they first sign in. Without this the invite context was silently
     dropped and the invitee had to be found again by e-mail. */
  let linkRequestId = $state<number | null>(null);
  /* Who sent the invitation and what they take the invitee for — shown as a
     banner so the page says "X invited you" instead of a generic greeting. */
  let invitedBy = $state("");
  let inviteType = $state<string | null>(null);
  /* The invitation attaches only to the address it was sent to; locking the
     field stops the invitee from typing another one and orphaning it. */
  let emailLocked = $state(false);
  const SIGNUP_TYPES = ["manufacturer", "wholesaler", "shop"];
  /** Invited to buy from the sender: the short form with the workspace question. */
  const clientInvite = $derived(linkRequestId !== null && inviteType === "shop");

  function consumeInviteParams() {
    const qIndex = window.location.hash.indexOf("?");
    if (qIndex === -1) return;
    const params = new URLSearchParams(window.location.hash.slice(qIndex + 1));
    const pEmail = params.get("email");
    const pCompany = params.get("company");
    const pCountry = (params.get("country") ?? "").toUpperCase();
    const pLr = Number(params.get("lr"));
    const pLang = params.get("lang");
    const pBy = (params.get("by") ?? "").trim();
    const pType = (params.get("type") ?? "").trim();
    if (pEmail) email = pEmail;
    if (pCompany) companyName = pCompany;
    if (pCountry.length === 2) {
      country = pCountry;
      countryAutoDetect = false;
    }
    if (Number.isFinite(pLr) && pLr > 0) {
      linkRequestId = pLr;
      invitedBy = pBy;
      emailLocked = !!pEmail;
      if (SIGNUP_TYPES.includes(pType)) inviteType = pType;
    }
    // Open the page in the language the invitation was written in.
    if (pLang && LANGUAGES.some((l) => l.code === pLang)) locale.set(pLang);
  }

  function useDifferentEmail() {
    emailLocked = false;
    linkRequestId = null;
    invitedBy = "";
    inviteType = null;
    email = "";
  }

  /** The browser's own region ("en-LV" → "LV") when the geo lookup is down —
   *  better than everyone defaulting to the first hard-coded country. */
  function navigatorCountry(): string | null {
    const tag = navigator.language || "";
    const region = tag.split("-")[1];
    return region && region.length === 2 ? region.toUpperCase() : null;
  }

  onMount(async () => {
    consumeInviteParams();
    countries = await fetchCountries().catch(() => [] as CountryRef[]);
    const detected = (await detectCountry()) || navigatorCountry();
    if (detected && countryAutoDetect) {
      // Only accept a code we actually offer. The reference API speaks alpha-3
      // while the dropdown values (and tenant.country) are alpha-2, so compare
      // on the converted code — not on `country.code`.
      const offered = countries.some((c) => countryAlpha2(c.code) === detected);
      if (countries.length === 0 || offered) {
        country = detected;
      }
    }
  });

  async function onSubmit(e: Event) {
    e.preventDefault();
    error = null;
    if (!companyName.trim() || !email.trim() || !password) {
      error = $_("signup.error.required");
      return;
    }
    if (clientInvite && (!firstName.trim() || !lastName.trim() || !phone.trim())) {
      error = $_("signup.error.required");
      return;
    }
    if (clientInvite && !workspace) {
      error = $_("signup.ws.required");
      return;
    }
    if (password.length < 12) {
      error = $_("auth.error.passwordTooShort");
      return;
    }
    if (password !== confirmPassword) {
      error = $_("auth.error.passwordsDoNotMatch");
      return;
    }
    if (!acceptTerms) {
      error = $_("consent.required");
      return;
    }
    loading = true;
    try {
      // The backend provisions the tenant + owner but deliberately does NOT
      // open a session: the account is unverified until the emailed code is
      // answered, so the next stop is always the code wall. Signing up again
      // with an address that is still unverified just re-sends the code.
      await api.post<{ slug?: string; pending_verification: boolean; email: string }>("/signup", {
        email: email.trim(),
        password,
        company_name: companyName.trim(),
        requested_company_type: inviteType ?? DEFAULT_COMPANY_TYPE,
        full_name: clientInvite ? `${firstName.trim()} ${lastName.trim()}` : fullName.trim(),
        phone: clientInvite ? phone.trim() : undefined,
        workspace: clientInvite ? workspace : undefined,
        country,
        language: $locale ?? "en",
        link_request_id: linkRequestId ?? undefined,
        accept_terms: acceptTerms,
      });
      setPendingVerificationEmail(email.trim());
      push("/verify-email");
    } catch (err) {
      error = err instanceof ApiError ? err.message : $_("signup.error.generic");
    } finally {
      loading = false;
    }
  }
</script>

<div class="page">
  <AuthHero />

  <section class="form">
      <div class="form-inner">
        <div class="lang-row">
          <a href="#/login" class="mobile-brand" aria-label="Order Evolution"><img src="/logo.svg" alt="Order Evolution" /></a>
          <LanguageSwitcher />
        </div>
      <form onsubmit={onSubmit} class="card signup-card">
        <h2>{$_("signup.title")}</h2>
        <p class="muted">{$_("signup.subtitle")}</p>
        {#if invitedBy}
          <div class="invite-banner" role="status">
            <span class="material-icons-outlined" aria-hidden="true">handshake</span>
            <span>{$_("signup.invitedBy", { values: { company: invitedBy } })}</span>
          </div>
        {/if}

        <div class="field">
          <label for="su-company">{$_("signup.companyName")}</label>
          <input id="su-company" type="text" bind:value={companyName} placeholder={$_("signup.companyName.placeholder")} required />
        </div>

        {#if clientInvite}
          <div class="row2">
            <div class="field">
              <label for="su-first">{$_("signup.firstName")}</label>
              <input id="su-first" type="text" autocomplete="given-name" bind:value={firstName} required />
            </div>
            <div class="field">
              <label for="su-last">{$_("signup.lastName")}</label>
              <input id="su-last" type="text" autocomplete="family-name" bind:value={lastName} required />
            </div>
          </div>
          <div class="row2">
            <div class="field">
              <label for="su-phone">{$_("signup.phone")}</label>
              <input id="su-phone" type="tel" autocomplete="tel" bind:value={phone} placeholder="+371 …" required />
            </div>
            <div class="field">
              <label for="su-country">{$_("signup.country")}</label>
              <select id="su-country" bind:value={country} onchange={() => (countryAutoDetect = false)}>
                {#each countryOptions as option}
                  <option value={option.value}>{option.label}</option>
                {/each}
              </select>
            </div>
          </div>
        {:else}
        <div class="row2">
          <div class="field">
            <label for="su-name">{$_("signup.fullName")}</label>
            <input id="su-name" type="text" autocomplete="name" bind:value={fullName} placeholder={$_("signup.fullName.placeholder")} />
          </div>
          <div class="field">
            <label for="su-country">{$_("signup.country")}</label>
            <select
              id="su-country"
              bind:value={country}
              onchange={() => (countryAutoDetect = false)}
            >
              {#each countryOptions as option}
                <option value={option.value}>{option.label}</option>
              {/each}
            </select>
          </div>
        </div>
        {/if}

        <div class="field">
          <label for="su-email">{$_("signup.email")}</label>
          <input id="su-email" type="email" autocomplete="email" bind:value={email} placeholder={$_("auth.email.placeholder")} readonly={emailLocked} required />
          {#if emailLocked}
            <p class="field-hint">
              {$_("signup.invitedEmailLocked")}
              <button type="button" class="link-btn" onclick={useDifferentEmail}>{$_("signup.useDifferentEmail")}</button>
            </p>
          {/if}
        </div>

        <div class="field">
          <label for="su-password">{$_("signup.password")}</label>
          <div class="pw-wrap">
            <input
              id="su-password"
              type={showPassword ? "text" : "password"}
              autocomplete="new-password"
              bind:value={password}
              placeholder={$_("signup.password.placeholder")}
              required
              minlength="12"
            />
            <button
              type="button"
              class="pw-toggle"
              aria-label={showPassword ? $_("auth.password.hide") : $_("auth.password.show")}
              aria-pressed={showPassword}
              title={showPassword ? $_("auth.password.hide") : $_("auth.password.show")}
              onclick={() => (showPassword = !showPassword)}
            >
              <span class="material-icons-outlined" aria-hidden="true">
                {showPassword ? "visibility_off" : "visibility"}
              </span>
            </button>
          </div>
        </div>

        <div class="field">
          <label for="su-password2">{$_("signup.confirmPassword")}</label>
          <div class="pw-wrap">
            <input
              id="su-password2"
              type={showConfirm ? "text" : "password"}
              autocomplete="new-password"
              bind:value={confirmPassword}
              placeholder={$_("signup.password.placeholder")}
              required
              minlength="12"
              class:input--invalid={passwordsMismatch}
              aria-invalid={passwordsMismatch}
              aria-describedby={passwordsMismatch ? "su-password2-err" : undefined}
            />
            <button
              type="button"
              class="pw-toggle"
              aria-label={showConfirm ? $_("auth.password.hide") : $_("auth.password.show")}
              aria-pressed={showConfirm}
              title={showConfirm ? $_("auth.password.hide") : $_("auth.password.show")}
              onclick={() => (showConfirm = !showConfirm)}
            >
              <span class="material-icons-outlined" aria-hidden="true">
                {showConfirm ? "visibility_off" : "visibility"}
              </span>
            </button>
          </div>
          {#if passwordsMismatch}
            <p id="su-password2-err" class="field-err">{$_("auth.error.passwordsDoNotMatch")}</p>
          {/if}
        </div>

        {#if clientInvite}
          <fieldset class="ws">
            <legend>{$_("signup.ws.question")}</legend>
            <label class="ws-opt" class:ws-opt--on={workspace === "orders"}>
              <input type="radio" name="su-ws" value="orders" bind:group={workspace} />
              <span>
                <span class="ws-title">{$_("signup.ws.orders.title", { values: { company: invitedBy } })}</span>
                <span class="ws-desc">{$_("signup.ws.orders.desc")}</span>
              </span>
            </label>
            <label class="ws-opt" class:ws-opt--on={workspace === "explore"}>
              <input type="radio" name="su-ws" value="explore" bind:group={workspace} />
              <span>
                <span class="ws-title">{$_("signup.ws.explore.title")}</span>
                <span class="ws-desc">{$_("signup.ws.explore.desc")}</span>
              </span>
            </label>
          </fieldset>
        {/if}

        <label class="consent">
          <input type="checkbox" bind:checked={acceptTerms} required />
          <ConsentText />
        </label>

        {#if error}<div class="err">{error}</div>{/if}

        <button
          type="submit"
          class="btn btn--primary btn--block"
          disabled={loading || passwordsMismatch || !acceptTerms}
        >
          {loading ? $_("signup.submitting") : $_("signup.submit")}
        </button>

        <p class="bottom">{$_("signup.haveAccount")} <a href="#/login">{$_("auth.signIn")}</a></p>
      </form>
      </div>
  </section>
</div>

<style>
  .ws { border: 0; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
  .ws legend { font-size: 13px; font-weight: 600; color: var(--color-surface-darker); margin-bottom: 8px; padding: 0; }
  .ws-opt {
    display: flex; gap: 10px; align-items: flex-start; cursor: pointer;
    padding: 10px 12px; border: 1px solid var(--color-divider); border-radius: 8px; background: #fff;
  }
  .ws-opt--on { border-color: var(--color-primary); background: rgba(33,133,213,0.05); }
  .ws-opt input { margin-top: 3px; flex-shrink: 0; }
  .ws-opt > span { display: flex; flex-direction: column; gap: 2px; }
  .ws-title { font-size: 13.5px; font-weight: 600; color: var(--color-surface-darker); }
  .ws-desc { font-size: 12px; color: var(--color-text-muted); line-height: 1.4; }
  .invite-banner {
    display: flex; align-items: flex-start; gap: 8px;
    margin: 0 0 14px; padding: 10px 12px; border-radius: 8px;
    background: rgba(33,133,213,0.08); color: #1b4f7a; font-size: 13px; line-height: 1.4;
  }
  .invite-banner .material-icons-outlined { color: var(--color-primary); font-size: 20px; }
  .field-hint { margin: 6px 0 0; font-size: 12px; color: #6b7680; }
  .field-hint .link-btn {
    background: none; border: 0; padding: 0; margin-left: 4px;
    color: var(--color-primary); text-decoration: underline; cursor: pointer; font-size: 12px;
  }
  input[readonly] { background: #f6f7f8; color: #4a545c; }
  .page {
    min-height: 100vh;
    display: grid;
    grid-template-columns: minmax(0, 11fr) minmax(0, 9fr);
    background: var(--color-bg);
  }
  /* The hero scrolls (eight blocks + advantages); the form stays put beside it. */
  .form { position: sticky; top: 0; align-self: start; min-height: 100vh; }
  @media (max-width: 900px) {
    .page { grid-template-columns: 1fr; }
    /* Phones: the form first, the story below it. */
    .form { order: 0; position: static; min-height: 0; }
    :global(.page > .hero) { order: 1; }
  }


  .form { display: flex; align-items: center; justify-content: center; padding: 32px; overflow-y: auto; }
  .form-inner { width: 100%; max-width: 480px; }
  .lang-row { display: flex; justify-content: flex-end; align-items: center; margin-bottom: 12px; }
  /* Phones: the hero (and its logo) sits below the form, so the brand goes
     here, at the top-left of the light column. Desktop has it in the hero. */
  .mobile-brand { display: none; margin-right: auto; }
  .mobile-brand img { height: 36px; width: auto; display: block; }
  @media (max-width: 900px) { .mobile-brand { display: block; } }
  .signup-card {
    width: 100%; max-width: 480px;
    padding: 32px;
    display: flex; flex-direction: column; gap: 16px;
  }
  .signup-card h2 { margin: 0; font-size: 24px; color: var(--color-surface-darker); text-align: center; }
  .signup-card .muted { text-align: center; margin: 0; }

  .field { display: flex; flex-direction: column; gap: 6px; }
  .row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: 480px) { .row2 { grid-template-columns: 1fr; } }

  /* Password field with the reveal toggle sitting inside the input. */
  .pw-wrap { position: relative; display: flex; }
  .pw-wrap input { width: 100%; padding-right: 42px; }
  .pw-toggle {
    position: absolute; right: 4px; top: 50%; transform: translateY(-50%);
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; padding: 0;
    border: none; background: none; cursor: pointer; border-radius: 50%;
    color: var(--color-text-muted);
  }
  .pw-toggle:hover { color: var(--color-primary); background: rgba(33,133,213,0.08); }
  .pw-toggle .material-icons-outlined { font-size: 20px; }

  .input--invalid { border-color: var(--color-danger); }
  .field-err { margin: 0; font-size: 12px; color: var(--color-danger); }
  .consent { display: flex; gap: 10px; align-items: flex-start; font-size: 13px; line-height: 1.45; color: var(--color-text); cursor: pointer; }
  .consent input { margin-top: 2px; flex-shrink: 0; }

  .bottom { text-align: center; margin: 4px 0 0; font-size: 13px; color: var(--color-text-muted); }
  .err {
    background: var(--color-danger-soft);
    border: 1px solid var(--color-danger);
    color: var(--color-danger);
    padding: 8px 12px; border-radius: var(--radius); font-size: 13px;
  }
</style>
